import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { apiFetch } from '../bizsuite-app';
import { toast } from '../utils/toast';
import SearchableSelect from './SearchableSelect';

/**
 * ActionsEditor — reusable Action Plan table for any entity that has an
 * action checklist (Opportunity, Lead, Customer …).
 *
 * Props:
 *   entityType   'opportunity' | 'lead' | 'customer'
 *   entityId     UUID — the parent record's id
 *   stage        current stage of the parent (used to filter templates)
 *   readOnly     if true, no add/edit/delete (e.g. for closed opportunities)
 *
 * Wire-up:
 *   GET    /api/actions?entityType=&entityId=
 *   POST   /api/actions
 *   PUT    /api/actions/:id
 *   DELETE /api/actions/:id
 *   PUT    /api/actions/:id/confirm
 *   GET    /api/action-templates?stage=
 *
 * Each row has: Stage / Sequence / Mode / Action Step (with template
 * typeahead) / By (multi-employee picker) / Reminder (preset) / Due Date
 * (auto-resolved or hand-picked) / Time (preset) / Comments / Status pill
 * with Confirm button.
 */

const MODES = ['In Person', 'Virtual', 'Mail', 'Others'];

// Reminder presets — labels are user-facing; keys go to the API.
// Order: Set Date on top (escape hatch), then chronological forward.
// We pass `sort={false}` to SearchableSelect so this order is preserved.
const REMINDER_PRESETS = [
  { v: 'set_date',      l: 'Set Date (Date Picker)' },
  { v: 'today',         l: 'Today' },
  { v: 'tomorrow',      l: 'Tomorrow' },
  { v: 'day_after',     l: 'Day After Tomorrow' },
  { v: 'next_monday',   l: 'Next Monday' },
  { v: 'next_tuesday',  l: 'Next Tuesday' },
  { v: 'next_wednesday',l: 'Next Wednesday' },
  { v: 'next_thursday', l: 'Next Thursday' },
  { v: 'next_friday',   l: 'Next Friday' },
  { v: 'next_saturday', l: 'Next Saturday' },
  { v: 'next_week',     l: 'Next Week' },
];

const TIME_PRESETS = [
  { v: 'set_time', l: 'Set Time (Time Picker)' },
  { v: '09:00',    l: '9:00 AM' },
  { v: '11:00',    l: '11:00 AM' },
  { v: '14:00',    l: '2:00 PM' },
  { v: '16:00',    l: '4:00 PM' },
  { v: '18:00',    l: '6:00 PM' },
  { v: '20:00',    l: '8:00 PM' },
];

export default function ActionsEditor({ entityType, entityId, stage, readOnly = false }) {
  const [actions, setActions] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);

  // ─── Data loaders ─────────────────────────────────────────────
  const loadActions = useCallback(async () => {
    if (!entityId) { setActions([]); setLoading(false); return; }
    try {
      const r = await apiFetch(`/api/actions?entityType=${encodeURIComponent(entityType)}&entityId=${encodeURIComponent(entityId)}`);
      const d = await r.json().catch(() => ({}));
      if (r.ok) setActions(d.actions || []);
    } finally { setLoading(false); }
  }, [entityType, entityId]);

  useEffect(() => { loadActions(); }, [loadActions]);

  // Templates load on mount + whenever the parent stage changes —
  // server filters to (stage=null OR stage=current) so admin can have
  // generic templates that show up on every entity.
  useEffect(() => {
    (async () => {
      try {
        const url = stage ? `/api/action-templates?stage=${encodeURIComponent(stage)}` : '/api/action-templates';
        const r = await apiFetch(url);
        const d = await r.json().catch(() => ({}));
        if (r.ok) setTemplates(d.templates || []);
      } catch (_) { /* templates are optional */ }
    })();
  }, [stage]);

  // Employees for the "By" multi-picker. Lightweight — just id+name.
  useEffect(() => {
    (async () => {
      try {
        const r = await apiFetch('/api/employees?limit=500');
        const d = await r.json().catch(() => ({}));
        if (r.ok) {
          const list = (d.employees || d.data || []).map(e => ({
            id: e.id,
            name: [e.first_name, e.last_name].filter(Boolean).join(' ') || e.email || 'Employee',
          }));
          setEmployees(list);
        }
      } catch (_) { /* picker degrades to empty */ }
    })();
  }, []);

  // Pipeline stages for the Stage column. Falls back to a static list
  // so the column is usable even before pipeline_stages is seeded.
  useEffect(() => {
    (async () => {
      try {
        const r = await apiFetch('/api/crm/pipeline-stages');
        const d = await r.json().catch(() => ({}));
        if (r.ok && Array.isArray(d.stages) && d.stages.length) {
          setStages(d.stages.map(s => s.name));
          return;
        }
      } catch (_) { /* fall through */ }
      setStages(['New', 'Qualified', 'Proposal', 'Negotiation', 'Won', 'Lost']);
    })();
  }, []);

  // ─── CRUD helpers ─────────────────────────────────────────────
  async function addRow() {
    if (!entityId) { toast.error('Save the record first'); return; }
    try {
      const r = await apiFetch('/api/actions', {
        method: 'POST',
        body: JSON.stringify({
          entity_type: entityType,
          entity_id: entityId,
          stage: stage || null,
          sequence: (actions.length + 1),
          action_step: 'New action',
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        // Surface the underlying error to the user — silent failures here
        // were the cause of the early "actions don't save" reports.
        const msg = d.error || `Add failed (HTTP ${r.status})`;
        console.error('[ActionsEditor] add failed:', d);
        toast.error(msg);
        return;
      }
      setActions(a => [...a, d.action]);
      toast.success('Action added');
    } catch (e) {
      console.error('[ActionsEditor] add threw:', e);
      toast.error(e.message || 'Network error');
    }
  }

  async function patchRow(id, patch) {
    const prev = actions.find(a => a.id === id);
    // Optimistic update — keep the UI snappy. Roll back if the PUT fails.
    setActions(a => a.map(x => x.id === id ? { ...x, ...patch } : x));
    const r = await apiFetch(`/api/actions/${id}`, { method: 'PUT', body: JSON.stringify(patch) });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) {
      setActions(a => a.map(x => x.id === id ? prev : x));
      toast.error(d.error || 'Save failed');
      return;
    }
    // Server may have re-resolved due_date from a preset — sync back.
    setActions(a => a.map(x => x.id === id ? { ...x, ...d.action } : x));
  }

  async function deleteRow(id) {
    if (!window.confirm('Delete this action?')) return;
    const r = await apiFetch(`/api/actions/${id}`, { method: 'DELETE' });
    if (!r.ok) { toast.error('Delete failed'); return; }
    setActions(a => a.filter(x => x.id !== id));
  }

  async function confirmRow(id) {
    const r = await apiFetch(`/api/actions/${id}/confirm`, { method: 'PUT' });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { toast.error(d.error || 'Confirm failed'); return; }
    setActions(a => a.map(x => x.id === id ? d.action : x));
    toast.success('Marked as done');
  }

  // ─── Render ───────────────────────────────────────────────────
  const [detailOpen, setDetailOpen] = useState(null);   // action.id of the row being edited in modal

  if (loading) return <div style={{ padding: 16, color: '#94A3B8' }}>Loading actions…</div>;
  if (!entityId) return <div style={{ padding: 16, color: '#64748B', fontSize: 12.5 }}>Save the record first to start adding actions.</div>;

  return (
    <div>
      {/* Card list — each action becomes a 2-line card so the row never
          scrolls horizontally off-screen. Inline edits are limited to the
          most-changed fields (action_step + assignees + status). For the
          full edit form (Stage / Mode / Reminder / Time / Comments / etc.)
          users click the pencil icon to open the detail modal. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {actions.length === 0 && (
          <div style={emptyCell}>No actions yet — click + Add Action to start.</div>
        )}
        {actions.map(a => (
          <ActionCard
            key={a.id}
            action={a}
            employees={employees}
            readOnly={readOnly}
            onPatch={p => patchRow(a.id, p)}
            onDelete={() => deleteRow(a.id)}
            onConfirm={() => confirmRow(a.id)}
            onOpenDetail={() => setDetailOpen(a.id)}
          />
        ))}
      </div>
      {!readOnly && (
        <button onClick={addRow} style={btnAdd}>+ Add Action</button>
      )}

      {/* Detail modal — full edit of all 9 fields */}
      {detailOpen && (
        <ActionDetailModal
          action={actions.find(x => x.id === detailOpen)}
          stages={stages}
          templates={templates}
          employees={employees}
          readOnly={readOnly}
          onClose={() => setDetailOpen(null)}
          onPatch={p => patchRow(detailOpen, p)}
        />
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
// Compact card view — sequence + action step + assignee chips + due
// pill on line 1; status + stage/mode badges + action icons on line 2.
// No horizontal scroll. Click the pencil icon to open the detail modal.
function ActionCard({ action, employees, readOnly, onPatch, onDelete, onConfirm, onOpenDetail }) {
  const isFired = action.status === 'fired';
  const isConfirmed = action.status === 'confirmed';
  const assigneeNames = useMemo(() => {
    const ids = action.assignee_employee_ids || [];
    return employees.filter(e => ids.includes(e.id)).map(e => e.name);
  }, [employees, action.assignee_employee_ids]);

  const dueLabel = action.due_date
    ? new Date(action.due_date).toISOString().slice(0, 10)
      + (action.time_value ? ' · ' + (action.time_value.slice(0, 5)) : '')
    : '—';

  return (
    <div style={{
      padding: '12px 14px', borderRadius: 10,
      border: '1px solid rgba(255,255,255,0.08)',
      background: 'rgba(255,255,255,0.03)',
      opacity: isConfirmed ? 0.55 : 1,
    }}>
      {/* Line 1 — sequence + action step + due chip */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 26, height: 26, borderRadius: 999, background: 'rgba(0,224,255,0.10)',
          border: '1px solid rgba(0,224,255,0.40)', color: '#6FE6FF', fontSize: 11.5, fontWeight: 700, flexShrink: 0,
        }}>{action.sequence ?? '—'}</div>
        <input value={action.action_step || ''} disabled={readOnly}
          onChange={e => onPatch({ action_step: e.target.value })}
          onBlur={e => onPatch({ action_step: e.target.value })}
          style={{ ...inp, flex: 1, minWidth: 200, fontSize: 13, fontWeight: 500 }}
          placeholder="What needs to happen?" />
        <span style={chipMuted} title="Due date / time">⏱ {dueLabel}</span>
      </div>

      {/* Line 2 — badges (Stage / Mode / By chips) + status + icons */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
        {action.stage && <span style={chipMuted}>{action.stage}</span>}
        {action.mode && <span style={chipMuted}>{action.mode}</span>}
        {assigneeNames.length > 0 && (
          <span style={chipMuted}>
            👥 {assigneeNames.length === 1 ? assigneeNames[0] : `${assigneeNames[0]} +${assigneeNames.length - 1}`}
          </span>
        )}
        <StatusPill status={action.status} />
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          {!readOnly && (isFired || action.status === 'pending') && (
            <button onClick={onConfirm} style={btnConfirm}>Mark done</button>
          )}
          {!readOnly && (
            <button onClick={onOpenDetail} style={iconBtn} title="Edit details" aria-label="Edit details">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
            </button>
          )}
          {!readOnly && (
            <button onClick={onDelete} style={iconBtnDanger} title="Delete" aria-label="Delete">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
// Detail modal — full edit of every field, opened from the pencil icon.
// All edits go through onPatch (PUT /api/actions/:id) so changes save
// immediately. Closing the modal just hides it; nothing to "save" because
// the underlying state is already persisted.
function ActionDetailModal({ action, stages, templates, employees, readOnly, onClose, onPatch }) {
  const stageOpts    = useMemo(() => stages.map(s => ({ value: s, label: s })), [stages]);
  const modeOpts     = useMemo(() => MODES.map(m => ({ value: m, label: m })), []);
  const reminderOpts = useMemo(() => REMINDER_PRESETS.map(p => ({ value: p.v, label: p.l })), []);
  const timeOpts     = useMemo(() => TIME_PRESETS.map(p => ({ value: p.v, label: p.l })), []);
  const templateOpts = useMemo(() => templates.map(t => ({ value: t.id, label: t.name })), [templates]);

  function pickTemplate(tplId) {
    const t = templates.find(x => x.id === tplId);
    if (!t) return;
    onPatch({ template_id: t.id, action_step: t.description || t.name, mode: t.default_mode || action.mode });
  }
  if (!action) return null;

  return createPortal(
    <div style={modalBackdrop} onClick={onClose}>
      <div style={modalBody} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h2 style={{ margin: 0, fontSize: 16 }}>Action Details</h2>
          <button onClick={onClose} style={iconBtn} aria-label="Close" title="Close">×</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          <ModalField label="Stage">
            <SearchableSelect value={action.stage || ''} onChange={v => onPatch({ stage: v })}
              options={stageOpts} placeholder="Pick stage" disabled={readOnly} />
          </ModalField>
          <ModalField label="Sequence">
            <input type="number" value={action.sequence ?? ''} disabled={readOnly}
              onChange={e => onPatch({ sequence: e.target.value === '' ? null : parseInt(e.target.value, 10) })}
              style={inp} />
          </ModalField>
          <ModalField label="Mode">
            <SearchableSelect value={action.mode || ''} onChange={v => onPatch({ mode: v })}
              options={modeOpts} placeholder="Pick mode" disabled={readOnly} />
          </ModalField>
          <ModalField label="Pick from Template">
            <SearchableSelect value={action.template_id || ''} onChange={pickTemplate}
              options={templateOpts} placeholder={templates.length ? 'Pick a template' : '(no templates yet)'}
              disabled={readOnly || !templates.length} />
          </ModalField>
          <ModalField label="Action Step" wide>
            <textarea value={action.action_step || ''} disabled={readOnly} rows={2}
              onChange={e => onPatch({ action_step: e.target.value })}
              style={{ ...inp, fontFamily: 'inherit', resize: 'vertical' }}
              placeholder="What needs to happen?" />
          </ModalField>
          <ModalField label="Assigned To" wide>
            <PeoplePicker value={action.assignee_employee_ids || []}
              onChange={ids => onPatch({ assignee_employee_ids: ids })}
              employees={employees} disabled={readOnly} />
          </ModalField>
          <ModalField label="Reminder">
            <SearchableSelect value={action.reminder_preset || ''} onChange={v => onPatch({ reminder_preset: v })}
              options={reminderOpts} placeholder="Pick when" disabled={readOnly} sort={false} />
          </ModalField>
          <ModalField label="Due Date">
            {action.reminder_preset === 'set_date' || !action.reminder_preset ? (
              <input type="date" value={action.due_date ? action.due_date.slice(0, 10) : ''}
                disabled={readOnly}
                onChange={e => onPatch({ due_date: e.target.value || null })} style={inp} />
            ) : (
              <div style={{ ...inp, color: '#94A3B8' }}>{action.due_date ? new Date(action.due_date).toISOString().slice(0, 10) : '—'}</div>
            )}
          </ModalField>
          <ModalField label="Time">
            <SearchableSelect value={action.time_preset || ''} onChange={v => onPatch({ time_preset: v })}
              options={timeOpts} placeholder="Pick time" disabled={readOnly} sort={false} />
          </ModalField>
          {action.time_preset === 'set_time' && (
            <ModalField label="Time (custom)">
              <input type="time" value={(action.time_value || '').slice(0, 5)}
                disabled={readOnly}
                onChange={e => onPatch({ time_value: e.target.value || null })} style={inp} />
            </ModalField>
          )}
          <ModalField label="Comments" wide>
            <textarea value={action.comments || ''} disabled={readOnly} rows={2}
              onChange={e => onPatch({ comments: e.target.value })}
              style={{ ...inp, fontFamily: 'inherit', resize: 'vertical' }}
              placeholder="Notes…" />
          </ModalField>
        </div>
        <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={btnDone}>Done</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function ModalField({ label, children, wide }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, gridColumn: wide ? '1 / -1' : 'auto' }}>
      <span style={{ fontSize: 10.5, color: '#94A3B8', letterSpacing: '0.4px', textTransform: 'uppercase', fontWeight: 600 }}>{label}</span>
      {children}
    </label>
  );
}

// (Old ActionRow / table-based renderer removed — superseded by ActionCard
//  which fits in two lines and offloads detail editing to ActionDetailModal.)

// ───────────────────────────────────────────────────────────────────
// Inline multi-employee picker. Shows chips for selected and a portaled
// dropdown to add. The portal is essential — the parent table uses
// overflow-x:auto for horizontal scrolling, which clips any absolutely
// positioned children. Rendering into document.body sidesteps that.
function PeoplePicker({ value, onChange, employees, disabled }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos]   = useState(null);    // { top, left, width }
  const [q, setQ]       = useState('');
  const anchorRef       = useRef(null);
  const dropdownRef     = useRef(null);

  const selected = useMemo(() => employees.filter(e => (value || []).includes(e.id)), [employees, value]);
  const candidates = useMemo(() => {
    const s = q.trim().toLowerCase();
    return employees
      .filter(e => !(value || []).includes(e.id))
      .filter(e => !s || (e.name || '').toLowerCase().includes(s))
      .slice(0, 30);
  }, [employees, value, q]);

  // Compute fixed-positioning coords whenever we open. Using getBoundingClientRect
  // means the dropdown sits flush under the anchor in viewport space — survives
  // any number of overflow-clipping ancestors.
  function openMenu() {
    if (!anchorRef.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    // Flip up if anchor is too close to the bottom of the viewport
    const willOverflow = r.bottom + 240 > window.innerHeight;
    setPos({
      top: willOverflow ? Math.max(8, r.top - 240 - 4) : r.bottom + 4,
      left: r.left,
      width: Math.max(220, r.width),
    });
    setOpen(true);
    setQ('');
  }

  // Close on click-outside (anchor + dropdown both excluded)
  useEffect(() => {
    if (!open) return;
    function onDocClick(e) {
      if (anchorRef.current && anchorRef.current.contains(e.target)) return;
      if (dropdownRef.current && dropdownRef.current.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  function add(id) { onChange([...(value || []), id]); setQ(''); }
  function remove(id) { onChange((value || []).filter(x => x !== id)); }

  return (
    <div ref={anchorRef} style={{ position: 'relative' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
        {selected.map(e => (
          <span key={e.id} style={chip}>
            {e.name}
            {!disabled && <button onClick={() => remove(e.id)} style={chipX}>×</button>}
          </span>
        ))}
        {!disabled && (
          <button onClick={open ? () => setOpen(false) : openMenu} style={btnAddPerson}>+</button>
        )}
      </div>
      {open && !disabled && pos && createPortal(
        <div ref={dropdownRef} style={{
          position: 'fixed', top: pos.top, left: pos.left,
          minWidth: pos.width, maxWidth: 360, zIndex: 9999,
          background: '#0F172A', border: '1px solid rgba(255,255,255,0.10)',
          borderRadius: 8, padding: 6, boxShadow: '0 12px 28px rgba(0,0,0,0.45)',
        }}>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search…" autoFocus style={{ ...inp, marginBottom: 6 }} />
          <div style={{ maxHeight: 200, overflowY: 'auto' }}>
            {candidates.length === 0 && <div style={{ padding: 8, color: '#94A3B8', fontSize: 12 }}>No matches</div>}
            {candidates.map(e => (
              <div key={e.id} onClick={() => add(e.id)} style={dropOpt}>{e.name}</div>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

function StatusPill({ status }) {
  let label, color, bg, border;
  switch (status) {
    case 'confirmed':
      label = 'Done'; color = '#10B981'; bg = 'rgba(16,185,129,0.15)'; border = 'rgba(16,185,129,0.40)'; break;
    case 'fired':
      label = 'Sent'; color = '#F59E0B'; bg = 'rgba(245,158,11,0.15)'; border = 'rgba(245,158,11,0.40)'; break;
    case 'cancelled':
      label = 'Cancelled'; color = '#94A3B8'; bg = 'rgba(148,163,184,0.15)'; border = 'rgba(148,163,184,0.40)'; break;
    default:
      label = 'Pending'; color = '#6FE6FF'; bg = 'rgba(0,224,255,0.10)'; border = 'rgba(0,224,255,0.40)';
  }
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999,
      color, background: bg, border: `1px solid ${border}`, textTransform: 'uppercase', letterSpacing: 0.4,
    }}>{label}</span>
  );
}

// ─── Styles ─────────────────────────────────────────────────────
const emptyCell = {
  padding: '24px 12px', textAlign: 'center', color: '#64748B', fontSize: 12.5, fontStyle: 'italic',
  borderRadius: 10, border: '1px dashed rgba(255,255,255,0.10)',
};
const inp = {
  padding: '6px 8px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.10)',
  background: 'rgba(15,23,42,0.6)', color: '#E5E7EB', fontSize: 12.5, outline: 'none', width: '100%',
};
const btnAdd = {
  marginTop: 12, padding: '8px 14px', borderRadius: 8, fontSize: 12.5, fontWeight: 600,
  border: '1px solid rgba(0,224,255,0.40)', background: 'rgba(0,224,255,0.10)', color: '#6FE6FF', cursor: 'pointer',
};
const btnConfirm = {
  padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
  border: '1px solid rgba(16,185,129,0.40)', background: 'rgba(16,185,129,0.10)', color: '#10B981', cursor: 'pointer',
};
const btnDone = {
  padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer',
  background: 'linear-gradient(135deg, #00E0FF, #3B8FF4)', color: '#fff',
};
// Modern square icon-only buttons used for Edit / Delete / Close in the cards + modal.
const iconBtn = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 28, height: 28, padding: 0, borderRadius: 6,
  border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.04)',
  color: '#94A3B8', cursor: 'pointer',
};
const iconBtnDanger = {
  ...{}, // keep shape consistent
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 28, height: 28, padding: 0, borderRadius: 6,
  border: '1px solid rgba(244,63,94,0.40)', background: 'rgba(244,63,94,0.10)',
  color: '#FB7185', cursor: 'pointer',
};
// Compact muted chip used inside ActionCard (Stage / Mode / By / Due summary).
const chipMuted = {
  display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 999,
  background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)',
  color: '#CBD5E1', fontSize: 11, fontWeight: 600,
};
// Cyan-tinted chip used by PeoplePicker for selected employees.
const chip = {
  display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 6px', borderRadius: 999,
  background: 'rgba(0,224,255,0.10)', border: '1px solid rgba(0,224,255,0.40)', color: '#6FE6FF', fontSize: 11, fontWeight: 600,
};
const chipX = { marginLeft: 2, background: 'transparent', border: 'none', color: '#6FE6FF', cursor: 'pointer', fontSize: 13, padding: 0, lineHeight: 1 };
const btnAddPerson = {
  padding: '2px 8px', borderRadius: 999, fontSize: 14, fontWeight: 700,
  border: '1px dashed rgba(255,255,255,0.20)', background: 'rgba(255,255,255,0.04)', color: '#94A3B8', cursor: 'pointer',
};
const dropOpt = { padding: '6px 8px', borderRadius: 6, cursor: 'pointer', fontSize: 12.5, color: '#E5E7EB' };
// Modal overlay + body — used for ActionDetailModal. Renders via React portal.
const modalBackdrop = {
  position: 'fixed', inset: 0, zIndex: 9000,
  background: 'rgba(2, 6, 23, 0.70)', backdropFilter: 'blur(2px)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
};
const modalBody = {
  width: 'min(720px, 100%)', maxHeight: '90vh', overflowY: 'auto',
  background: '#0B1020', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 14, padding: 18,
  boxShadow: '0 20px 60px rgba(0,0,0,0.55)',
};
